import { useEffect, useState, useMemo } from "react";
import { api, formatError, formatCurrency, formatDate } from "../lib/api";
import { apiWithOffline } from "../lib/offlineQueue";
import { PageHeader, Modal, StatusBadge } from "../components/ui-kit";
import { Plus, Trash2, Eye, Package, Router as RouterIcon, PlusCircle, PackageMinus, Download, Pencil } from "lucide-react";
import { useAuth } from "../lib/auth";
import QRScannerButton from "../components/QRScannerButton";
import { BACKEND_URL } from "../lib/api";

const EMPTY_FORM = {
  client_name: "", client_address: "", technician_id: "",
  service_type_id: "", notes: "", status: "aberta",
  materials: [], equipment_unit_ids: [], complements: [], collected_equipment: []
};

export default function WorkOrdersPage() {
  const { user } = useAuth();
  const isTech = user?.role === "tecnico";
  const [ots, setOts] = useState([]);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);

  const [techs, setTechs] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [stock, setStock] = useState([]);
  const [techUnits, setTechUnits] = useState([]);
  const [complementsCatalog, setComplementsCatalog] = useState([]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  // Advanced filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterTechId, setFilterTechId] = useState("");

  // Tick a state every 60s so the tech's edit-window countdown re-renders live.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!isTech) return;
    const t = setInterval(() => setNowTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, [isTech]);

  // OT edit history (loaded when detail modal opens)
  const [history, setHistory] = useState([]);
  const [searchText, setSearchText] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    if (!isTech && filterTechId) params.append("technician_id", filterTechId);
    api.get(`/work-orders?${params.toString()}`).then((r) => setOts(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, fromDate, toDate, filterTechId]);

  useEffect(() => {
    api.get("/service-types").then((r) => setServiceTypes(r.data));
    api.get("/complements").then((r) => setComplementsCatalog(r.data));
    if (!isTech) api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "tecnico")));
  }, [isTech]);

  const loadTechData = (tid) => {
    if (!tid) { setStock([]); setTechUnits([]); return; }
    api.get(`/technician-stock?technician_id=${tid}`).then((r) => setStock(r.data));
    api.get(`/equipment-units?technician_id=${tid}&status=com_tecnico`).then((r) => setTechUnits(r.data));
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, technician_id: isTech ? user.id : "" });
    setEquipRows([]);
    setError(""); setOpen(true);
    if (isTech) {
      api.get("/technician-stock").then((r) => setStock(r.data));
      api.get("/equipment-units?status=com_tecnico").then((r) => setTechUnits(r.data));
    }
  };

  const onSelectTech = (tid) => {
    setForm({ ...form, technician_id: tid, materials: [], equipment_unit_ids: [] });
    loadTechData(tid);
  };

  // Materials
  const rowKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const addMaterial = () => setForm({ ...form, materials: [...form.materials, { _rid: rowKey(), material_id: "", quantity: 1, serial_number: "" }] });
  const updateMat = (i, patch) => {
    const arr = [...form.materials]; arr[i] = { ...arr[i], ...patch }; setForm({ ...form, materials: arr });
  };
  const removeMat = (i) => setForm({ ...form, materials: form.materials.filter((_, x) => x !== i) });

  // Equipment rows — each row has {_rid, model_id, unit_id, sn_input}
  // and we derive equipment_unit_ids from populated rows on submit.
  const [equipRows, setEquipRows] = useState([]);
  const addEquipRow = () => setEquipRows((rows) => [...rows, { _rid: rowKey(), model_id: "", unit_id: "", sn_input: "" }]);
  const updateEquipRow = (i, patch) => setEquipRows((rows) => {
    const arr = [...rows]; arr[i] = { ...arr[i], ...patch }; return arr;
  });
  const removeEquipRow = (i) => setEquipRows((rows) => rows.filter((_, x) => x !== i));

  // Try to auto-resolve when user types an SN directly
  const resolveSnDirect = (i, raw) => {
    const sn = (raw || "").trim();
    if (!sn) { updateEquipRow(i, { sn_input: sn }); return; }
    const found = techUnits.find((u) => (u.serial_number || "").toLowerCase() === sn.toLowerCase());
    if (found) {
      updateEquipRow(i, { sn_input: sn, unit_id: found.id, model_id: found.model_id });
    } else {
      updateEquipRow(i, { sn_input: sn, unit_id: "" });
    }
  };

  // Keep form.equipment_unit_ids in sync with equipRows
  useEffect(() => {
    const ids = Array.from(new Set(equipRows.map((r) => r.unit_id).filter(Boolean)));
    setForm((f) => (
      JSON.stringify(f.equipment_unit_ids) === JSON.stringify(ids) ? f : { ...f, equipment_unit_ids: ids }
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipRows]);

  // Legacy checkbox helper (kept in case something imports it — unused now)
  const toggleUnit = (uid) => {
    const set = new Set(form.equipment_unit_ids);
    if (set.has(uid)) set.delete(uid); else set.add(uid);
    setForm({ ...form, equipment_unit_ids: Array.from(set) });
  };

  // Complements
  const addComplement = () => setForm({ ...form, complements: [...form.complements, { _rid: rowKey(), complement_id: "", quantity: 1 }] });
  const updateComp = (i, patch) => {
    const arr = [...form.complements]; arr[i] = { ...arr[i], ...patch }; setForm({ ...form, complements: arr });
  };
  const removeComp = (i) => setForm({ ...form, complements: form.complements.filter((_, x) => x !== i) });

  // Collected equipment (from client -> back to warehouse via technician)
  const [equipmentModels, setEquipmentModels] = useState([]);
  useEffect(() => { api.get("/equipment-models").then((r) => setEquipmentModels(r.data)); }, []);
  const addCollected = () => setForm({ ...form, collected_equipment: [...form.collected_equipment, { _rid: rowKey(), model_id: "", model_name: "", serial_number: "", notes: "" }] });
  const updateCollected = (i, patch) => {
    const arr = [...form.collected_equipment]; arr[i] = { ...arr[i], ...patch }; setForm({ ...form, collected_equipment: arr });
  };
  const removeCollected = (i) => setForm({ ...form, collected_equipment: form.collected_equipment.filter((_, x) => x !== i) });

  const svc = serviceTypes.find((s) => s.id === form.service_type_id);
  const previewTotal = useMemo(() => {
    let total = svc?.value || 0;
    for (const m of form.materials) {
      const mat = stock.find((s) => s.material_id === m.material_id);
      // stock endpoint doesn't include price; fetch from materials — approximate as 0 in preview
      total += 0; // material price is applied server-side; UI preview shows service + complements
    }
    for (const c of form.complements) {
      const comp = complementsCatalog.find((x) => x.id === c.complement_id);
      total += (comp?.value || 0) * (Number(c.quantity) || 0);
    }
    return total;
  }, [svc, form.materials, form.complements, stock, complementsCatalog]);

  const save = async () => {
    setError("");
    try {
      const payload = {
        ...form,
        materials: form.materials
          .filter((m) => m.material_id)
          .map((m) => ({ material_id: m.material_id, quantity: Number(m.quantity), serial_number: m.serial_number || "" })),
        complements: form.complements
          .filter((c) => c.complement_id)
          .map((c) => ({ complement_id: c.complement_id, quantity: Number(c.quantity) })),
        collected_equipment: form.collected_equipment
          .filter((ce) => (ce.serial_number || "").trim())
          .map((ce) => ({
            model_id: ce.model_id || null,
            model_name: ce.model_name || "",
            serial_number: ce.serial_number.trim(),
            notes: ce.notes || "",
          })),
      };
      if (editing) {
        // Edit path — collected_equipment não editável aqui (evita duplicação)
        await api.put(`/work-orders/${editing.id}`, {
          client_name: payload.client_name,
          client_address: payload.client_address,
          service_type_id: payload.service_type_id,
          notes: payload.notes,
          materials: payload.materials,
          complements: payload.complements,
        });
        setOpen(false); setEditing(null); load();
        return;
      }
      const result = await apiWithOffline("post", "/work-orders", payload, "Nova OT");
      setOpen(false);
      if (result.queued) {
        alert("Sem conexão no momento. OT foi salva offline e será enviada assim que a internet voltar.");
      } else {
        load();
      }
    } catch (e) { setError(formatError(e)); }
  };

  const changeStatus = async (id, s) => {
    await api.put(`/work-orders/${id}/status`, { status: s });
    load();
    if (view?.id === id) setView({ ...view, status: s });
  };

  const openView = async (o) => {
    setView(o);
    setHistory([]);
    try {
      const r = await api.get(`/work-orders/${o.id}/history`);
      setHistory(r.data?.edit_history || []);
    } catch (e) { console.warn("ot history fetch failed", e); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta OT?")) return;
    await api.delete(`/work-orders/${id}`);
    load();
  };

  const exportExcel = async () => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    if (!isTech && filterTechId) params.append("technician_id", filterTechId);
    const token = localStorage.getItem("ot_token");
    const url = `${BACKEND_URL}/api/work-orders/export.xlsx?${params.toString()}`;
    // Fetch with auth header, then download
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Falha ao exportar");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = res.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] || "OTs.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { alert("Erro ao exportar: " + e.message); }
  };

  const clearFilters = () => { setStatus(""); setFromDate(""); setToDate(""); setFilterTechId(""); };

  const openEdit = (o) => {
    // Only allow edit for non-cancelled
    if (o.status === "cancelada") { alert("OT cancelada não pode ser editada"); return; }
    setEditing(o);
    setForm({
      client_name: o.client_name || "",
      client_address: o.client_address || "",
      technician_id: o.technician_id,
      service_type_id: o.service_type_id,
      notes: o.notes || "",
      status: o.status,
      materials: (o.materials || []).map((m) => ({ _rid: rowKey(), material_id: m.material_id, quantity: m.quantity, serial_number: m.serial_number || "" })),
      equipment_unit_ids: (o.equipment || []).map((e) => e.unit_id),
      complements: (o.complements || []).map((c) => ({ _rid: rowKey(), complement_id: c.complement_id, quantity: c.quantity })),
      collected_equipment: [],
    });
    // Reconstruct equipRows for editing
    setEquipRows(
      (o.equipment || []).map((e) => ({
        _rid: rowKey(),
        model_id: e.model_id || "",
        unit_id: e.unit_id,
        sn_input: e.serial_number || "",
      }))
    );
    if (o.technician_id) loadTechData(o.technician_id);
    setError(""); setOpen(true);
  };

  // Técnicos podem editar suas próprias OTs até 48h depois de criadas.
  // Depois disso, apenas admin/gestor podem modificar (sem cronómetro).
  const EDIT_WINDOW_MS = 48 * 60 * 60 * 1000;
  const techCanStillEdit = (ot) => {
    if (!ot?.date) return true;
    const created = Date.parse(ot.date);
    if (isNaN(created)) return true;
    return Date.now() - created < EDIT_WINDOW_MS;
  };

  // Return remaining edit window as a compact string like "47h 12m" or "23m" or null when expired/unknown.
  const techEditTimeLeft = (ot) => {
    if (!ot?.date) return null;
    const created = Date.parse(ot.date);
    if (isNaN(created)) return null;
    const remaining = EDIT_WINDOW_MS - (Date.now() - created);
    if (remaining <= 0) return null;
    const totalMinutes = Math.floor(remaining / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  // Local search: number, client name/address, technician name.
  const filteredOts = ots.filter((o) => {
    const q = searchText.trim().toLowerCase();
    if (!q) return true;
    return [o.number, o.client_name, o.client_address, o.technician_name, o.service_type_name]
      .some((f) => (f || "").toString().toLowerCase().includes(q));
  });

  return (
    <div data-testid="ots-page">
      <PageHeader
        eyebrow="Operações"
        title="Ordens de Trabalho"
        actions={<>
          <button className="btn btn-secondary" onClick={exportExcel} data-testid="export-ots-xlsx"><Download size={14} /> Excel</button>
          <button className="btn btn-primary" onClick={openNew} data-testid="new-ot-btn"><Plus size={16} /> Nova OT</button>
        </>}
      />

      <div className="card mb-4 p-4 flex gap-3 items-end flex-wrap">
        <div>
          <span className="label-caps block mb-1">Status</span>
          <select className="select w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ot-status-filter">
            <option value="">Todos</option>
            <option value="aberta">Aberta</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="concluida">Concluída</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
        <div>
          <span className="label-caps block mb-1">De</span>
          <input type="date" className="input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="ot-filter-from" />
        </div>
        <div>
          <span className="label-caps block mb-1">Até</span>
          <input type="date" className="input" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="ot-filter-to" />
        </div>
        {!isTech && (
          <div>
            <span className="label-caps block mb-1">Técnico</span>
            <select className="select w-52" value={filterTechId} onChange={(e) => setFilterTechId(e.target.value)} data-testid="ot-filter-tech">
              <option value="">Todos</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        {(status || fromDate || toDate || filterTechId || searchText) && (
          <button className="btn btn-ghost" onClick={() => { clearFilters(); setSearchText(""); }} data-testid="ot-clear-filters">Limpar filtros</button>
        )}
        <div className="flex-1 min-w-[220px]">
          <span className="label-caps block mb-1">Buscar</span>
          <input
            type="search"
            className="input"
            placeholder="Nº OT, cliente, técnico, endereço…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            data-testid="ot-search-input"
          />
        </div>
        <div className="ml-auto text-xs" style={{ color: "var(--text-mute)" }}>
          {filteredOts.length} OT(s) · Excel respeita os filtros ativos
        </div>
      </div>

      <div className="card">
        <table className="data">
          <thead><tr><th>OT</th><th>Data</th><th>Cliente</th><th>Técnico</th><th>Serviço</th><th>Valor</th><th>Status</th><th>Ações</th></tr></thead>
          <tbody>
            {filteredOts.map((o) => {
              const canEdit = o.status !== "cancelada"
                && (o.status !== "concluida" || !isTech)
                && (!isTech || techCanStillEdit(o));
              return (
              <tr key={o.id} data-testid={`ot-row-${o.number}`}>
                <td className="mono">{o.number}</td>
                <td>{formatDate(o.date)}</td>
                <td>{o.client_name}</td>
                <td>{o.technician_name}</td>
                <td>{o.service_type_name}</td>
                <td className="mono">{formatCurrency(o.total_value)}</td>
                <td><StatusBadge status={o.status} /></td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <button className="btn btn-ghost" onClick={() => openView(o)} data-testid={`view-ot-${o.number}`} title="Ver detalhes"><Eye size={14} /></button>
                    {canEdit && (
                      <button className="btn btn-ghost" onClick={() => openEdit(o)} title="Editar OT" data-testid={`edit-ot-${o.number}`}><Pencil size={14} /> Editar</button>
                    )}
                    {isTech && canEdit && techEditTimeLeft(o) && (
                      <span
                        className="text-[10px] mono"
                        style={{ color: "#FACC15" }}
                        title={`Tempo restante para editar: ${techEditTimeLeft(o)}`}
                        data-testid={`ot-edit-timer-${o.number}`}
                      >
                        ⏱ {techEditTimeLeft(o)}
                      </span>
                    )}
                    {isTech && o.status !== "cancelada" && !canEdit && (
                      <span className="text-[10px]" style={{ color: "var(--text-mute)" }} title="Passou o prazo de 48h desde a criação. Peça ao gestor.">
                        Bloqueada (48h)
                      </span>
                    )}
                    {!isTech && <button className="btn btn-ghost" onClick={() => remove(o.id)} title="Excluir OT"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
              );
            })}
            {filteredOts.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Nenhuma OT.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); }} title={editing ? `Editar OT ${editing.number}` : "Nova Ordem de Trabalho"} testid="ot-modal"
        footer={<>
          <div className="flex-1 text-sm" style={{ color: "var(--text-dim)" }}>
            Valor prévio: <b className="mono" style={{ color: "var(--text)" }}>{formatCurrency(previewTotal)}</b>
            <span className="text-xs ml-2" style={{ color: "var(--text-mute)" }}>(materiais somados no servidor)</span>
          </div>
          <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="save-ot-btn">Criar OT</button>
        </>}>
        {error && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{error}</div>}

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div><label className="label-caps block mb-1">Cliente</label><input className="input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="ot-client" /></div>
          <div><label className="label-caps block mb-1">Endereço</label><input className="input" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} data-testid="ot-address" /></div>
          {!isTech && (
            <div><label className="label-caps block mb-1">Técnico</label>
              <select className="select" value={form.technician_id} onChange={(e) => onSelectTech(e.target.value)} data-testid="ot-tech">
                <option value="">Selecione</option>
                {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div><label className="label-caps block mb-1">Tipo de Serviço</label>
            <select className="select" value={form.service_type_id} onChange={(e) => setForm({ ...form, service_type_id: e.target.value })} data-testid="ot-service-type">
              <option value="">Selecione</option>
              {serviceTypes.map((s) => <option key={s.id} value={s.id}>{s.name} — {formatCurrency(s.value)}</option>)}
            </select>
          </div>
          <div><label className="label-caps block mb-1">Status</label>
            <select className="select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="ot-status-new">
              <option value="aberta">Aberta</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="concluida">Concluída</option>
            </select>
          </div>
        </div>

        {/* Materials */}
        <Section title="Materiais Utilizados" icon={<Package size={14} />} action={
          <button type="button" className="btn btn-secondary" onClick={addMaterial} data-testid="add-material-ot"><Plus size={14} /> Adicionar</button>
        }>
          <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>Consumíveis do estoque do técnico. <b style={{ color: "var(--primary)" }}>Baixa automática ao criar.</b></div>
          {form.materials.length === 0 && <EmptyLine text="Nenhum material adicionado." />}
          {form.materials.map((m, i) => {
            const stockItem = stock.find((s) => s.material_id === m.material_id);
            return (
              <div key={m._rid || `mat-${i}`} className="grid grid-cols-12 gap-2 mb-2 items-end">
                <div className="col-span-7">
                  <select className="select" value={m.material_id} onChange={(e) => updateMat(i, { material_id: e.target.value })}>
                    <option value="">Selecione material</option>
                    {stock.map((s) => <option key={s.material_id} value={s.material_id}>{s.material_code} — {s.material_name} (disp: {s.quantity})</option>)}
                  </select>
                </div>
                <div className="col-span-2"><input className="input mono" type="number" min="1" max={stockItem?.quantity ?? 999} value={m.quantity} onChange={(e) => updateMat(i, { quantity: e.target.value })} placeholder="Qtd" /></div>
                <div className="col-span-2"><input className="input mono" placeholder="SN opcional" value={m.serial_number} onChange={(e) => updateMat(i, { serial_number: e.target.value })} /></div>
                <div className="col-span-1"><button className="btn btn-ghost" onClick={() => removeMat(i)} type="button"><Trash2 size={14} /></button></div>
              </div>
            );
          })}
        </Section>

        {/* Equipment */}
        <Section title="Equipamentos Instalados" icon={<RouterIcon size={14} />} action={
          <button type="button" className="btn btn-secondary" onClick={addEquipRow} data-testid="add-equipment-ot" disabled={!form.technician_id}><Plus size={14} /> Adicionar</button>
        }>
          <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>
            Escolha o modelo e o Nº de Série, ou <b>digite o SN direto</b> para carregar automaticamente. <b style={{ color: "var(--primary)" }}>Serão marcados como "Instalado" ao criar.</b>
          </div>
          {!form.technician_id && <EmptyLine text="Selecione um técnico primeiro." />}
          {form.technician_id && techUnits.length === 0 && <EmptyLine text="Este técnico não tem equipamentos disponíveis." />}
          {form.technician_id && equipRows.length === 0 && techUnits.length > 0 && (
            <EmptyLine text="Nenhum equipamento adicionado. Clique em Adicionar." />
          )}
          {equipRows.map((r, i) => {
            const modelsWithUnits = Array.from(new Set(techUnits.map((u) => u.model_id))).map((mid) => {
              const m = equipmentModels.find((x) => x.id === mid);
              return { id: mid, name: m?.name || techUnits.find((u) => u.model_id === mid)?.model_name || "Modelo" };
            });
            // Units for the chosen model (or all if not chosen), excluding ones already picked in other rows
            const usedIds = new Set(equipRows.filter((_, x) => x !== i).map((x) => x.unit_id).filter(Boolean));
            const unitOptions = techUnits
              .filter((u) => (!r.model_id || u.model_id === r.model_id) && !usedIds.has(u.id));
            return (
              <div key={r._rid} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`equip-row-${i}`}>
                <div className="col-span-4">
                  {i === 0 && <span className="label-caps block mb-1">Modelo</span>}
                  <select className="select" value={r.model_id} onChange={(e) => updateEquipRow(i, { model_id: e.target.value, unit_id: "", sn_input: "" })} data-testid={`equip-model-${i}`}>
                    <option value="">Selecione</option>
                    {modelsWithUnits.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div className="col-span-4">
                  {i === 0 && <span className="label-caps block mb-1">Nº de Série</span>}
                  <select className="select mono" value={r.unit_id} onChange={(e) => {
                    const u = techUnits.find((x) => x.id === e.target.value);
                    updateEquipRow(i, { unit_id: e.target.value, sn_input: u?.serial_number || "", model_id: u?.model_id || r.model_id });
                  }} data-testid={`equip-unit-${i}`}>
                    <option value="">Selecione</option>
                    {unitOptions.map((u) => <option key={u.id} value={u.id}>{u.serial_number}</option>)}
                  </select>
                </div>
                <div className="col-span-3">
                  {i === 0 && <span className="label-caps block mb-1">Ou digite o SN</span>}
                  <div className="flex gap-1">
                    <input
                      className={`input mono flex-1 ${r.sn_input && !r.unit_id ? "border-red-500" : ""}`}
                      style={{ borderColor: r.sn_input && !r.unit_id ? "#F87171" : undefined }}
                      value={r.sn_input}
                      placeholder="Digite SN…"
                      onChange={(e) => resolveSnDirect(i, e.target.value)}
                      data-testid={`equip-sn-input-${i}`}
                    />
                    <QRScannerButton onScan={(text) => resolveSnDirect(i, text)} testid={`equip-qr-${i}`} />
                  </div>
                  {r.sn_input && !r.unit_id && (
                    <div className="text-[10px] mt-0.5" style={{ color: "#F87171" }}>SN não encontrado no técnico</div>
                  )}
                  {r.unit_id && (
                    <div className="text-[10px] mt-0.5" style={{ color: "#4ADE80" }}>✓ carregado</div>
                  )}
                </div>
                <div className="col-span-1">
                  <button type="button" className="btn btn-ghost" onClick={() => removeEquipRow(i)} data-testid={`equip-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </Section>

        {/* Complements */}
        <Section title="Complementos (Adicionais)" icon={<PlusCircle size={14} />} action={
          <button type="button" className="btn btn-secondary" onClick={addComplement} data-testid="add-complement-ot"><Plus size={14} /> Adicionar</button>
        }>
          <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>
            Cada complemento adiciona seu valor ao total (ex.: Decodificador Adicional, Repetidor de Sinal).
          </div>
          {form.complements.length === 0 && <EmptyLine text="Nenhum complemento adicionado." />}
          {form.complements.map((c, i) => {
            const comp = complementsCatalog.find((x) => x.id === c.complement_id);
            const subtotal = (comp?.value || 0) * (Number(c.quantity) || 0);
            return (
              <div key={c._rid || `comp-${i}`} className="grid grid-cols-12 gap-2 mb-2 items-center">
                <div className="col-span-6">
                  <select className="select" value={c.complement_id} onChange={(e) => updateComp(i, { complement_id: e.target.value })} data-testid={`complement-select-${i}`}>
                    <option value="">Selecione complemento</option>
                    {complementsCatalog.map((cc) => <option key={cc.id} value={cc.id}>{cc.name} — {formatCurrency(cc.value)}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><input className="input mono" type="number" min="1" value={c.quantity} onChange={(e) => updateComp(i, { quantity: e.target.value })} placeholder="Qtd" /></div>
                <div className="col-span-3 mono text-right pr-2 text-sm" style={{ color: "var(--text-dim)" }}>{formatCurrency(subtotal)}</div>
                <div className="col-span-1"><button className="btn btn-ghost" onClick={() => removeComp(i)} type="button"><Trash2 size={14} /></button></div>
              </div>
            );
          })}
        </Section>

        {/* Collected Equipment (from client -> warehouse) */}
        <Section title="Devolução · Retirados do Cliente" icon={<PackageMinus size={14} />} action={
          <button type="button" className="btn btn-secondary" onClick={addCollected} data-testid="add-collected-ot"><Plus size={14} /> Adicionar</button>
        }>
          <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>
            Equipamentos <b>retirados do cliente</b> para levar ao armazém (ex.: roteador antigo, decodificador defeituoso). Ao criar a OT, cada SN passa a ficar <b style={{ color: "var(--primary)" }}>"com o técnico"</b>. Se o SN ainda não estiver cadastrado, será registrado automaticamente.
          </div>
          {form.collected_equipment.length === 0 && <EmptyLine text="Nenhum equipamento retirado." />}
          {form.collected_equipment.map((ce, i) => (
            <div key={ce._rid || `col-${i}`} className="grid grid-cols-12 gap-2 mb-2 items-end">
              <div className="col-span-4">
                <select className="select" value={ce.model_id} onChange={(e) => {
                  const m = equipmentModels.find((x) => x.id === e.target.value);
                  updateCollected(i, { model_id: e.target.value, model_name: m?.name || "" });
                }} data-testid={`collected-model-${i}`}>
                  <option value="">Modelo (opcional)</option>
                  {equipmentModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div className="col-span-3">
                <input className="input mono" placeholder="Nº Série" value={ce.serial_number} onChange={(e) => updateCollected(i, { serial_number: e.target.value })} data-testid={`collected-sn-${i}`} />
              </div>
              <div className="col-span-1 flex justify-center">
                <QRScannerButton onScan={(text) => updateCollected(i, { serial_number: text })} testid={`collected-qr-${i}`} />
              </div>
              <div className="col-span-3">
                <input className="input" placeholder="Motivo / observação" value={ce.notes} onChange={(e) => updateCollected(i, { notes: e.target.value })} />
              </div>
              <div className="col-span-1"><button className="btn btn-ghost" onClick={() => removeCollected(i)} type="button"><Trash2 size={14} /></button></div>
            </div>
          ))}
        </Section>

        <div><label className="label-caps block mb-1">Observações</label>
          <textarea className="textarea" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </Modal>

      <Modal open={!!view} onClose={() => setView(null)} title={view?.number} testid="ot-detail-modal"
        footer={
          view && (
            <div className="flex gap-2 flex-wrap">
              {view.status !== "em_andamento" && <button className="btn btn-secondary" onClick={() => changeStatus(view.id, "em_andamento")} data-testid="status-em-andamento">Em Andamento</button>}
              {view.status !== "concluida" && <button className="btn btn-primary" onClick={() => changeStatus(view.id, "concluida")} data-testid="status-concluida">Concluir</button>}
              {view.status !== "cancelada" && <button className="btn btn-danger" onClick={() => changeStatus(view.id, "cancelada")} data-testid="status-cancelada">Cancelar</button>}
            </div>
          )
        }>
        {view && (
          <div className="text-sm space-y-2">
            <div><span className="label-caps">Cliente:</span> {view.client_name}</div>
            <div><span className="label-caps">Endereço:</span> {view.client_address || "-"}</div>
            <div><span className="label-caps">Técnico:</span> {view.technician_name}</div>
            <div><span className="label-caps">Serviço:</span> {view.service_type_name} — {formatCurrency(view.service_value)}</div>
            <div><span className="label-caps">Status:</span> <StatusBadge status={view.status} /></div>
            <div className="text-base"><span className="label-caps">Valor Total:</span> <span className="mono font-bold" style={{ color: "var(--primary)" }}>{formatCurrency(view.total_value)}</span></div>

            {view.materials?.length > 0 && (
              <div className="pt-2">
                <div className="label-caps mb-1">Materiais</div>
                <table className="data">
                  <thead><tr><th>Código</th><th>Nome</th><th>Nº Série</th><th>Qtd</th><th>Preço</th></tr></thead>
                  <tbody>
                    {view.materials.map((m, i) => (
                      <tr key={`${m.material_id || m.material_code || "mat"}-${i}`}>
                        <td className="mono">{m.material_code}</td>
                        <td>{m.material_name}</td>
                        <td className="mono">{m.serial_number || "-"}</td>
                        <td className="mono">{m.quantity}</td>
                        <td className="mono">{formatCurrency(m.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {view.equipment?.length > 0 && (
              <div className="pt-2">
                <div className="label-caps mb-1">Equipamentos Instalados</div>
                <table className="data">
                  <thead><tr><th>Modelo</th><th>Nº Série</th></tr></thead>
                  <tbody>
                    {view.equipment.map((e, i) => (
                      <tr key={e.unit_id || e.serial_number || `eq-${i}`}><td>{e.model_name}</td><td className="mono font-semibold">{e.serial_number}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {view.collected_equipment?.length > 0 && (
              <div className="pt-2">
                <div className="label-caps mb-1" style={{ color: "var(--warning)" }}>Retirados do Cliente (Devolução)</div>
                <table className="data">
                  <thead><tr><th>Modelo</th><th>Nº Série</th><th>Observação</th></tr></thead>
                  <tbody>
                    {view.collected_equipment.map((c, i) => (
                      <tr key={c.serial_number || `col-${i}`}>
                        <td>{c.model_name || "-"}</td>
                        <td className="mono font-semibold">{c.serial_number}</td>
                        <td className="text-xs" style={{ color: "var(--text-dim)" }}>{c.notes || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {view.complements?.length > 0 && (
              <div className="pt-2">
                <div className="label-caps mb-1">Complementos</div>
                <table className="data">
                  <thead><tr><th>Nome</th><th>Qtd</th><th>Valor Unit.</th><th>Subtotal</th></tr></thead>
                  <tbody>
                    {view.complements.map((c, i) => (
                      <tr key={`${c.complement_id || c.name || "cmp"}-${i}`}>
                        <td>{c.name}</td>
                        <td className="mono">{c.quantity}</td>
                        <td className="mono">{formatCurrency(c.value)}</td>
                        <td className="mono">{formatCurrency(c.value * c.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {view.notes && <div><span className="label-caps">Obs:</span> <span style={{ color: "var(--text-dim)" }}>{view.notes}</span></div>}

            <div className="pt-3">
              <div className="label-caps mb-1">Histórico de Edições</div>
              {history.length === 0 ? (
                <div className="text-xs italic p-2" style={{ color: "var(--text-mute)" }} data-testid="ot-history-empty">
                  Sem edições registradas para esta OT.
                </div>
              ) : (
                <div className="space-y-2" data-testid="ot-history-list">
                  {history.map((h, i) => (
                    <div key={`${h.at || "h"}-${i}`} className="p-2 text-xs border" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold">{h.user_name} <span className="label-caps" style={{ color: "var(--text-mute)" }}>· {h.user_role}</span></span>
                        <span className="mono" style={{ color: "var(--text-mute)" }}>{formatDate(h.at)}</span>
                      </div>
                      <ul className="pl-3 list-disc">
                        {Object.entries(h.changes || {}).map(([field, diff]) => (
                          <li key={field}>
                            <b>{field}</b>: <span style={{ color: "var(--text-mute)" }}>{String(diff?.from ?? "-")}</span>
                            {" → "}
                            <span style={{ color: "var(--text)" }}>{String(diff?.to ?? "-")}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Section({ title, icon, action, children }) {
  return (
    <div className="mb-5 pb-4" style={{ borderBottom: "1px solid var(--border)" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 label-caps">{icon} {title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyLine({ text }) {
  return <div className="text-xs italic p-2" style={{ color: "var(--text-mute)" }}>{text}</div>;
}
